import {
  PlusOutlined,
  PushpinOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { getGeocodingParticelleCatastali } from '@framework/api-client/api/geocoding/geocoding-controller/geocoding-controller.ts';
import { type GetGeocodingParticelleCatastaliParams } from '@framework/api-client/api/geocoding/schemas/getGeocodingParticelleCatastaliParams.ts';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { ManagedSubmitButton } from '@framework/form/form/submit/ManagedSubmitButton.tsx';
import { SubmitButton } from '@framework/form/form/submit/SubmitButton.tsx';
import { NumberInput } from '@framework/form/inputs/NumberInput.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { useFormItemEnabled } from '@framework/form/item/useFormItemEnabled.ts';
import { ModalDialog } from '@framework/ui/components/modal/ModalDialog.tsx';
import { RowElement } from '@framework/ui/components/row/RowElement.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { Button, message, Space, Tooltip } from 'antd';
import { type Map } from 'ol';
import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Subject } from 'rxjs';
import { type MapSubjectEvent } from '../MapWidgetComponent.tsx';
import { MapWidgetLogic } from '../MapWidgetLogic.ts';
import { geoJSONEpsg3857 } from '../mapStyleUtils.ts';
import { MapProvinceMunicipalitySelectInput } from './MapProvinceMunicipalitySelectInput.tsx';

export interface IMapSearchCadastralControlProps {
  map: React.RefObject<Map>;
  mapSubject: React.RefObject<Subject<MapSubjectEvent>>;
  municipalityGroupList?: string[];
  isMobile?: boolean | null;
}

const SearchCadastralControlSchema: yup.ObjectSchema<
  GetGeocodingParticelleCatastaliParams & {
    select: boolean;
    codiceProvincia: string;
  }
> = yup.object({
  codiceComune: yup.string().required().label('Comune'),
  codiceProvincia: yup.string().required().label('Provincia'),
  codiceSezione: yup.string().label('Sezione catastale'),
  codiceFoglio: yup
    .number()
    .transform(t => t ?? undefined)
    .label('Foglio catastale'),
  numero: yup
    .number()
    .transform(t => t ?? undefined)
    .label('Particella catastale'),
  codiceAllegato: yup.string().label('Allegato catastale'),
  codiceSviluppo: yup.string().label('Sviluppo catastale'),
  page: yup.number().default(0).label('Pagina'),
  maxNumResults: yup.number().default(10).label('Numero massimo di risultati'),
  select: yup.boolean().default(false).label('Seleziona'),
});

export function MapSearchCadastralControl(
  props: IMapSearchCadastralControlProps,
) {
  const { map, mapSubject, municipalityGroupList, isMobile } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { visuallyDisabled } = useFormItemEnabled();

  const buttonText = t(
    'mappa.controlli.cerca-particella.pulsante.testo',
    'Particella',
  );
  const dialogTitle = t(
    'mappa.controlli.cerca-particella.modale.titolo',
    'Cerca per particella catastale',
  );

  return (
    <div>
      {MapWidgetLogic.isCadastralEnabled() && (
        <Tooltip title={dialogTitle}>
          <Button
            style={{ borderWidth: '1px' }}
            disabled={visuallyDisabled}
            icon={<PushpinOutlined aria-hidden="true" />}
            onClick={() => setIsOpen(true)}
            aria-label={isMobile ? buttonText : undefined}
          >
            {!isMobile && buttonText}
          </Button>
        </Tooltip>
      )}

      <ModalDialog
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        size="medium"
        title={dialogTitle}
        closable
        footer={null}
      >
        <FormHandler
          schema={SearchCadastralControlSchema}
          onSubmit={async values => {
            try {
              const cadastralParticles =
                await getGeocodingParticelleCatastali(values);

              if (cadastralParticles.features?.length === 0) {
                message.info(
                  t(
                    'mappa.controlli.cerca-particella.modale.form.errore.nessun-risultato',
                    'Nessun risultato trovato',
                  ),
                );
                return;
              }

              const feature = geoJSONEpsg3857.readFeature(
                cadastralParticles.features?.[0],
              );
              if (!feature) {
                message.error(
                  t(
                    'mappa.controlli.cerca-particella.modale.form.errore.nessuna-geometria-valida',
                    'Errore durante la ricerca. Nessuna geometria valida trovata',
                  ),
                );
                // biome-ignore format: <tbd>
                console.error('Errore durante la ricerca. Nessuna geometria valida trovata', cadastralParticles);
                return;
              }

              map.current?.getView().fit(feature.getGeometry()!.getExtent(), {
                padding: [16, 16, 16, 16],
                duration: 400,
              });

              if (values.select) {
                // Seleziona la particella
                mapSubject.current?.next({
                  type: 'add',
                  feature: feature!,
                  addToSource: true,
                });
              }
              setIsOpen(false);
            } catch (error) {
              message.error(
                t(
                  'mappa.controlli.cerca-particella.modale.form.errore.durante-la-ricerca',
                  'Si è verificato un errore durante la ricerca',
                ),
              );
              console.error(error);
            }
          }}
          nested
          layout="vertical"
        >
          <div>
            <RowElement>
              <MapProvinceMunicipalitySelectInput
                name="codiceComune"
                provinceName="codiceProvincia"
                municipalityGroupList={municipalityGroupList}
              />
            </RowElement>
            <RowElement>
              <FormItem
                name="codiceSezione"
                label={t(
                  'mappa.controlli.cerca-particella.modale.form-label.sezione-catastale',
                  'Sezione',
                )}
              >
                <TextInput name="codiceSezione" />
              </FormItem>
              <FormItem
                name="codiceFoglio"
                label={t(
                  'mappa.controlli.cerca-particella.modale.form-label.foglio-catastale',
                  'Foglio',
                )}
              >
                <NumberInput name="codiceFoglio" />
              </FormItem>
              <FormItem
                name="numero"
                label={t(
                  'mappa.controlli.cerca-particella.modale.form-label.particella-catastale',
                  'Particella',
                )}
              >
                <NumberInput name="numero" />
              </FormItem>
            </RowElement>
            <RowElement>
              <FormItem
                name="codiceAllegato"
                label={t(
                  'mappa.controlli.cerca-particella.modale.form-label.allegato-catastale',
                  'Allegato',
                )}
              >
                <TextInput name="codiceAllegato" />
              </FormItem>
              <FormItem
                name="codiceSviluppo"
                label={t(
                  'mappa.controlli.cerca-particella.modale.form-label.sviluppo-catastale',
                  'Sviluppo',
                )}
              >
                <TextInput name="codiceSviluppo" />
              </FormItem>
            </RowElement>
            <Space>
              <SubmitButton
                type="default"
                block
                icon={<SearchOutlined aria-hidden />}
              >
                {t(
                  'mappa.controlli.cerca-particella.modale.form-button.cerca',
                  'Cerca',
                )}
              </SubmitButton>
              <ManagedSubmitButton
                transformOnSubmit={data => {
                  data.select = true;
                }}
                type="primary"
                size="large"
                iconLeftComponent={PlusOutlined}
              >
                {t(
                  'mappa.controlli.cerca-particella.modale.form-button.seleziona',
                  'Seleziona',
                )}
              </ManagedSubmitButton>
            </Space>
          </div>
        </FormHandler>
      </ModalDialog>
    </div>
  );
}
